import { FireMode } from "@common/constants";
import type { GunDefinition } from "@common/definitions/items/guns";

/**
 * What the combat sounds and effects are chosen by: pure rules, no audio or drawing here (tested in
 * tests/src/combatSounds.test.ts). The sounds themselves are made by tools/sfx/combat_sounds.py, in takes
 * (`impact_metal_1` to `_4`...): COMBAT_TAKES says how many each has, and a test checks every one is in the game.
 */

/** how many takes each combat sound has (in client/public/audio/game/shared/combat) */
export const COMBAT_TAKES = {
    flesh_hit: 4,
    helmet_hit: 3,
    vest_hit: 3,
    impact_metal: 4,
    impact_metal_heavy: 3,
    impact_stone: 4,
    impact_wood: 4,
    impact_tree: 2,
    impact_dirt: 4,
    impact_glass: 3,
    ricochet: 4,
    bullet_crack: 4,
    bullet_flyby: 4,
    brass_drop_hard: 4,
    brass_drop_wood: 2,
    brass_drop_soft: 2,
    shell_drop_hard: 3,
    casing_splash: 2,
    hit_confirm: 3,
    hit_confirm_armor: 2,
    kill_confirm: 2
} as const;

export type CombatSound = keyof typeof COMBAT_TAKES;

/** picks takes of a sound at random, never the same one twice running (the ear notices a repeat at once) */
export class TakePicker {
    private readonly _last = new Map<string, number>();

    pick(sound: CombatSound, random: () => number = Math.random): string {
        const takes = COMBAT_TAKES[sound];
        const last = this._last.get(sound);
        let take = 1 + Math.floor(random() * takes);
        if (takes > 1 && take === last) take = take % takes + 1;
        this._last.set(sound, take);
        return `${sound}_${take}`;
    }
}

// ------------------------------------------------------------------------------------------------------------- guns

export type GunClass = "pistol" | "smg" | "rifle" | "sniper" | "shotgun" | "launcher" | "toy";

export interface GunTraits {
    readonly gunClass: GunClass
    /** it fires quietly, without a flash: little smoke, no glow */
    readonly suppressed: boolean
    /**
     * it shows no muzzle flash (the definition's noMuzzleFlash), whatever its class: no glow, no sparks. A launcher's
     * smoke stays (the firework launcher's, say): 0.84's first build gave it a glow the game itself never shows
     */
    readonly flashless: boolean
    /** its bullets outrun sound: passing close, they crack instead of whooshing */
    readonly supersonic: boolean
    /** its casings' pitch landing (small pistol brass rings higher, big rifle brass lower) */
    readonly casingPitch: number
}

const PISTOL_CALIBRES = new Set(["9mm", "45acp", "50ae", "127mm", "500sw", "curadell"]);
const RIFLE_CALIBRES = new Set(["556mm", "762mm", "545mm", "50cal", "338lap", "9x39mm"]);
const BIG_CALIBRES = new Set(["50cal", "338lap"]);

/**
 * A gun's class for its effects and sounds, from what it fires and how: there's no class in the definitions. Shotguns
 * fire 12 gauge; launchers rockets and the like; the BB gun is a toy; a short gun is a pistol; pistol calibres from a
 * longer gun an SMG; a rifle firing single shots that hit very hard or fly very fast a sniper; anything else a rifle.
 */
export function gunTraits(def: Pick<GunDefinition, "ammoType" | "fireMode" | "length" | "noMuzzleFlash"> & { readonly ballistics: { readonly damage: number, readonly speed: number } }): GunTraits {
    const ammo = def.ammoType;
    const { damage, speed } = def.ballistics;
    let gunClass: GunClass;
    if (ammo === "bb") gunClass = "toy";
    else if (ammo === "12g") gunClass = "shotgun";
    else if (!PISTOL_CALIBRES.has(ammo) && !RIFLE_CALIBRES.has(ammo)) gunClass = "launcher";
    else if (def.length < 5.5) gunClass = "pistol";
    else if (PISTOL_CALIBRES.has(ammo)) gunClass = "smg";
    else if (def.fireMode === FireMode.Single && (damage >= 40 || speed >= 0.35)) gunClass = "sniper";
    else gunClass = "rifle";
    const rifleBullet = RIFLE_CALIBRES.has(ammo) && gunClass !== "pistol";
    return {
        gunClass,
        suppressed: def.noMuzzleFlash === true && gunClass !== "toy" && gunClass !== "launcher",
        flashless: def.noMuzzleFlash === true,
        supersonic: rifleBullet && speed >= 0.26,
        casingPitch: gunClass === "pistol" || gunClass === "smg" ? 1.12 : BIG_CALIBRES.has(ammo) || gunClass === "sniper" ? 0.88 : 1
    };
}

/** what a bullet passing close sounds like: a rifle's cracks, anything slower whooshes by */
export function nearMissSound(traits: GunTraits | undefined): CombatSound {
    return traits?.supersonic ? "bullet_crack" : "bullet_flyby";
}

// ---------------------------------------------------------------------------------------------------------- impacts

/** what a bullet strikes, for its sound and what flies off */
export type ImpactKind = "metal" | "metal_heavy" | "stone" | "ceramic" | "wood" | "tree" | "dirt" | "cardboard" | "soft" | "glass" | "foliage";

/** the obstacles' and buildings' materials (common/src/definitions/obstacles.ts: Materials), as a bullet meets them */
const MATERIAL_IMPACTS: Readonly<Record<string, ImpactKind>> = {
    metal_light: "metal",
    iron: "metal",
    appliance: "metal",
    metal_heavy: "metal_heavy",
    stone: "stone",
    porcelain: "ceramic",
    wood: "wood",
    crate: "wood",
    fence: "wood",
    cardboard: "cardboard",
    tree: "tree",
    sand: "dirt",
    trash_bag: "dirt",
    glass: "glass",
    ice: "glass",
    bush: "foliage"
};

/** a bullet's impact on a material (undefined: the material's own hit sound, as a pumpkin's or a piano's) */
/** what's soft whatever its material says: upholstery and mattresses (wood in the definitions), tyres and life rings (stone) */
const SOFT = /(^|_)(sofa|couch|armchair|bed|mattress|tire|life_preserver)(_|$)/u;

/**
 * a bullet's impact on a material (undefined: the material's own hit sound, as a pumpkin's or a piano's); `idString`, the
 * obstacle's, when it says more than its material (0.85: a sofa threw splinters, a tyre stone chips, a cardboard box sand)
 */
export function impactKindOf(material: string | undefined, idString?: string): ImpactKind | undefined {
    if (material === undefined) return undefined;
    const kind = MATERIAL_IMPACTS[material];
    if (kind !== undefined && kind !== "foliage" && idString !== undefined && SOFT.test(idString)) return "soft";
    return kind;
}

export interface ImpactSound {
    readonly sound: CombatSound
    /** playback speed (pitch): porcelain is stone rung higher */
    readonly speed: number
    readonly volume: number
}

/** foliage keeps the bush's own rustle */
export const IMPACT_SOUNDS: Readonly<Record<ImpactKind, ImpactSound | undefined>> = {
    metal: { sound: "impact_metal", speed: 1, volume: 1 },
    metal_heavy: { sound: "impact_metal_heavy", speed: 1, volume: 1 },
    stone: { sound: "impact_stone", speed: 1, volume: 1 },
    ceramic: { sound: "impact_stone", speed: 1.3, volume: 0.85 },
    wood: { sound: "impact_wood", speed: 1, volume: 1 },
    tree: { sound: "impact_tree", speed: 1, volume: 1 },
    dirt: { sound: "impact_dirt", speed: 1, volume: 1 },
    // (a box: a light, papery knock; a sofa or a tyre: a dull thump, nothing ringing)
    cardboard: { sound: "impact_wood", speed: 1.4, volume: 0.55 },
    soft: { sound: "impact_dirt", speed: 0.85, volume: 0.75 },
    glass: { sound: "impact_glass", speed: 1, volume: 1 },
    foliage: undefined
};

/** how often a bullet goes whining off a hard surface it doesn't pierce */
export function ricochetChance(kind: ImpactKind): number {
    switch (kind) {
        case "metal_heavy": return 0.22;
        case "metal": return 0.12;
        case "stone": return 0.1;
        default: return 0;
    }
}

// ---------------------------------------------------------------------------------------------------------- casings

export interface CasingSound {
    readonly sound: CombatSound
    readonly volume: number
}

/**
 * The sound of a casing landing on a floor (common/src/utils/terrain.ts: FloorNames), brass or a shotgun's shell:
 * brass rings on stone, metal and ice, rings shorter on floorboards, thuds on grass, sand and carpet, plips into
 * water. Nothing on the void.
 */
export function casingSound(floor: string, shell: boolean): CasingSound | undefined {
    switch (floor) {
        case "stone":
        case "metal":
        case "ice":
            return shell ? { sound: "shell_drop_hard", volume: 1 } : { sound: "brass_drop_hard", volume: 1 };
        case "wood":
        case "log":
            return shell ? { sound: "shell_drop_hard", volume: 0.7 } : { sound: "brass_drop_wood", volume: 1 };
        case "grass":
        case "sand":
        case "carpet":
            return { sound: "brass_drop_soft", volume: shell ? 1.2 : 1 };
        case "water":
            return { sound: "casing_splash", volume: 1 };
        default:
            return undefined;
    }
}

// ------------------------------------------------------------------------------------------------------- distance

/** the highest a lowpass is worth having: above it the filter changes nothing anyone hears */
export const AIR_OPEN = 16_000;

/**
 * Air absorbs the highs of a sound more the farther it travels (and what's in the way does too): a sound `distance`
 * away, of the most it can be heard at (`maxRange`), keeps the frequencies up to this. Near it's open (AIR_OPEN or
 * more: no filter); at half its range it's down to about 6.5 kHz; at the edge 1.5 kHz, a distant thud.
 */
export function airCutoff(distance: number, maxRange: number): number {
    const r = Math.min(1, Math.max(0, maxRange > 0 ? distance / maxRange : 1));
    return 20_000 * (1500 / 20_000) ** (r ** 1.2);
}

/**
 * How much of a sound reaches its surroundings' echo, by how loud it arrives directly (0 to 1, distance alone), to the
 * power `law` (acousticsLogic.echoMix: where the listener is). Below 1 the direct sound fades faster with distance than
 * the echo does: in a street or a room a far gunshot is more echo than a near one. In the open (0.9) it barely is: 0.84
 * used 0.5 everywhere, so a far shot out in the fields was more echo than shot. `amount`: the sound's own share
 * (gunshots a lot, impacts a little).
 */
export function roomSend(direct: number, amount: number, law = 0.9): number {
    return amount * Math.min(1, Math.max(0, direct)) ** law;
}

// --------------------------------------------------------------------------------------------------------- throttle

/**
 * Sounds that come in floods (a shotgun's nine pellets into one body, full auto into one wall, a magazine's casings)
 * would play as one sound many times over: louder, phasey, and costly. A key is "fresh" if it hasn't been allowed
 * within `gapMs`; and no more than `perWindow` of a kind are allowed in any `windowMs`.
 */
export class SoundThrottle {
    private readonly _last = new Map<string, number>();
    private readonly _recent: number[] = [];

    constructor(private readonly perWindow: number, private readonly windowMs: number) {}

    allow(key: string, now: number, gapMs: number): boolean {
        const last = this._last.get(key);
        if (last !== undefined && now - last < gapMs) return false;
        while (this._recent.length && now - this._recent[0] >= this.windowMs) this._recent.shift();
        if (this._recent.length >= this.perWindow) return false;
        this._recent.push(now);
        this._last.set(key, now);
        if (this._last.size > 256) {
            for (const [k, at] of this._last) if (now - at > 2000) this._last.delete(k);
        }
        return true;
    }
}
